import { computed, defineComponent, type PropType } from 'vue'

import styles from './index.module.less'

export type HoloButtonVariant =
  | 'primary'
  | 'ghost'
  | 'outline'
  | 'danger'
  | 'warning'
  | 'success'
  | 'icon'
  | 'text'

export type HoloButtonSize = 'sm' | 'md'
export type HoloButtonHtmlType = 'button' | 'submit' | 'reset'
export type HoloButtonIconPlacement = 'start' | 'end'

export default defineComponent({
  name: 'HoloButton',
  inheritAttrs: false,
  props: {
    variant: {
      type: String as PropType<HoloButtonVariant>,
      default: 'primary',
    },
    size: {
      type: String as PropType<HoloButtonSize>,
      default: 'md',
    },
    htmlType: {
      type: String as PropType<HoloButtonHtmlType>,
      default: 'button',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    block: {
      type: Boolean,
      default: false,
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
    iconPlacement: {
      type: String as PropType<HoloButtonIconPlacement>,
      default: 'start',
    },
  },
  emits: {
    click: (_event: MouseEvent) => true,
  },
  setup(props, { attrs, emit, slots }) {
    const isDisabled = computed(() => props.disabled || props.loading)

    function handleClick(event: MouseEvent): void {
      if (isDisabled.value) {
        event.preventDefault()
        event.stopPropagation()
        return
      }
      emit('click', event)
    }

    function renderLoadingIndicator() {
      return <span class={styles.loadingIndicator} aria-hidden="true" />
    }

    function renderIcon() {
      if (props.loading) return renderLoadingIndicator()
      return slots.icon?.()
    }

    return () => {
      const icon = renderIcon()
      const content = slots.default?.()
      const hasText = !props.iconOnly && content != null
      const iconBefore = props.iconPlacement === 'start'

      return (
        <button
          {...attrs}
          type={props.htmlType}
          class={[
            styles.button,
            styles[`variant-${props.variant}`],
            styles[`size-${props.size}`],
            props.block ? styles.block : '',
            props.iconOnly || props.variant === 'icon' ? styles.iconOnly : '',
            props.loading ? styles.loading : '',
            isDisabled.value ? styles.disabled : '',
            attrs.class,
          ]}
          disabled={isDisabled.value}
          aria-busy={props.loading ? 'true' : undefined}
          onClick={handleClick}
        >
          {iconBefore && icon ? <span class={styles.icon}>{icon}</span> : null}
          {hasText ? <span class={styles.label}>{content}</span> : null}
          {!iconBefore && icon ? <span class={styles.icon}>{icon}</span> : null}
        </button>
      )
    }
  },
})
